import { Box, Flex, Text } from '@everlywell/ui-kit';
import React, { useEffect, useMemo, useState } from 'react';

import {
  Promo,
  discountedPrice,
  promoAppliesToProduct,
} from '../../../utils/helpers/activePromoHelpers';
import PriceV2 from '../PriceV2';

type SubscriptionOptionType = {
  id: number;
  label: string;
  name: string;
  savingsStatement: string;
  percentDiscount: number;
  price: string;
};

export type PromoDiscountPriceProps = {
  price: number;
  activeSubscriptionOption?: SubscriptionOptionType;
  promo?: Promo;
  productId: number;
  promotionable?: boolean;
  memberPrice?: string;
  subscribeSelected: boolean;
  memberHeroCopy?: string;
  className?: string;
  isTextReduced?: boolean;
  promoText?: string;
  isOutOfStock?: boolean;
  displayCents?: boolean;
};

const PromoDiscountPrice = ({
  price,
  activeSubscriptionOption,
  promo,
  productId,
  promotionable,
  memberPrice,
  subscribeSelected,
  memberHeroCopy,
  className,
  isTextReduced,
  promoText,
  displayCents,
  isOutOfStock = false,
}: PromoDiscountPriceProps): JSX.Element => {
  const formattedPrice = displayCents
    ? (+price).toFixed(2)
    : (+price).toFixed(0);
  const formattedSubscriptionPrice = useMemo(
    () =>
      activeSubscriptionOption && (+activeSubscriptionOption.price).toFixed(2),
    [activeSubscriptionOption],
  );

  const promoCode = promo?.code;
  const discountedPromoPrice = discountedPrice(price, promo);
  const isZeroDiscountedPrice = +discountedPromoPrice === 0;
  const isPromoted =
    promo && promoAppliesToProduct(productId, promo, promotionable);

  /**
   * If product price after promo applied is 0, so a promo of 0%, do not show
   * the promo and strikethrough.
   * This is done to allow tiered promos that start with a range of 0% discount.
   */
  const isPriceDiscounted = +discountedPromoPrice < price;

  const showPromo =
    isPromoted &&
    !memberPrice &&
    !subscribeSelected &&
    !isZeroDiscountedPrice &&
    isPriceDiscounted;

  const [showDiscount, setShowDiscount] = useState<boolean>(false);
  const [hasPromoInfo, sethasPromoInfo] = useState<boolean>(false);

  useEffect(() => {
    setShowDiscount(subscribeSelected || !!memberPrice || !!showPromo);
    sethasPromoInfo(true);
  }, [subscribeSelected, memberPrice, showPromo]);

  const renderDiscountPriceDisplay = () => {
    if (subscribeSelected) {
      return `${formattedSubscriptionPrice}`;
    }
    if (memberPrice) {
      return memberPrice;
    }
    if (showPromo) {
      return `${discountedPromoPrice}`;
    }
    return null;
  };

  // volumeDiscounts flag removed - defaulted to false
  const volumeDiscountsEnabled = false;
  // everlywellPlusOnlyTest flag removed - defaulted to true (as an array)
  const everlywellPlusOnlyTest: number[] = [];
  const isEverlywellPlusOnlyTest = everlywellPlusOnlyTest?.includes(productId);

  const everlywellPlusPrice = isEverlywellPlusOnlyTest
    ? 'Available for $39/mo with Everlywell+ Membership'
    : undefined;

  const renderDiscountText = () => {
    if (subscribeSelected) {
      return activeSubscriptionOption?.savingsStatement;
    }
    if (memberHeroCopy) {
      return memberHeroCopy;
    }
    if (showPromo && volumeDiscountsEnabled) {
      return `add more to save more!`;
    }
    if (showPromo) {
      return `with code ${promoCode?.toUpperCase()}`;
    }
    return null;
  };

  const discountPriceDisplay = renderDiscountPriceDisplay();
  const discountText = renderDiscountText();

  return (
    <Box
      data-testid="pricing-wrapper"
      className={className}
      // @ts-expect-error: Box doesn't accept isTextReduced and showDiscount props
      isTextReduced={isTextReduced}
      showDiscount={showDiscount && !isOutOfStock}
    >
      {hasPromoInfo && showPromo && !isOutOfStock ? (
        <>
          <Flex gap="1" alignItems="center" data-testid="price-with-promo">
            <PriceV2
              dollarAmount={everlywellPlusPrice || formattedPrice}
              variant="strikethrough"
            />
            {discountPriceDisplay && (
              <PriceV2
                variant="discountedPrice"
                dollarAmount={discountPriceDisplay}
              />
            )}
          </Flex>

          {discountText && <Text color="utility.sale">{discountText}</Text>}
          {promoText && (
            <Text fontSize="sm" lineHeight="shorter">
              {promoText}
            </Text>
          )}
        </>
      ) : (
        /* Show the regular price initially, once the use effect hook runs we will have
      promo info updated and we can show the block above with an eventual strikethrough */
        <Box data-testid="price-without-promo">
          <PriceV2 dollarAmount={formattedPrice} />
        </Box>
      )}
    </Box>
  );
};

export default PromoDiscountPrice;
